import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Settings, Users, Clock, DollarSign, CheckCircle, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { API_URL } from '../config';
import '../styles/Courses.css';


const courseIcons = {
  'cyber-privacy-security-basics': Shield,
  'protect-devices': Settings,
  'small-business-security': Users
};

// Structured data for courses page
const coursesStructuredData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Online Privacy & Security Courses",
  "description": "Explore practical online courses covering cyber privacy basics, scam prevention, encrypted email, and tracking protection.",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Course",
        "name": "Cyber Privacy & Security Basics",
        "description": "A practical online course designed to help you reduce silent tracking and profiling, prevent scams, understand safety tools like VPN and reset your digital security.",
        "provider": {
          "@type": "Organization",
          "name": "All Secured",
          "url": "https://allsecured.com.au"
        },
        "url": "https://allsecured.com.au/courses/cyber-privacy-security-basics"
      }
    }
  ]
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/courses`, {
          withCredentials: true
        });
        setCourses(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load courses');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourses();
  }, []);

  const formatPrice = (cents, currency = 'AUD') => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="courses-page">
        <Header />
        <div className="courses-loading">
          <Loader2 size={48} className="spinner" />
          <p>Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="courses-page">
      <SEO 
        title="Online Privacy & Security Courses | Digital Safety Training | All Secured"
        description="Explore practical online courses covering cyber privacy basics, scam prevention, encrypted email, and tracking protection. Simple, clear, actionable digital safety training."
        canonical="/courses"
        structuredData={coursesStructuredData}
      />
      <Header />
      
      <section className="courses-hero">
        <div className="container">
          <h1>Online Privacy & Security Courses</h1>
          <h2>Digital Safety Training</h2>
          <p>Explore practical online courses covering cyber privacy basics, scam prevention, encrypted email, and tracking protection.</p>
        </div>
      </section>
      
      <section className="courses-list">
        <div className="container">
          {error && (
            <div className="courses-error">
              <p>{error}</p>
            </div>
          )}
          
          <div className="courses-grid">
            {courses.map((course) => {
              const IconComponent = courseIcons[course.slug] || Shield;
              return (
                <Link 
                  to={`/courses/${course.slug}`} 
                  key={course.id} 
                  className="course-card"
                >
                  {course.has_access && (
                    <div className="course-owned-badge">
                      <CheckCircle size={16} />
                      Owned
                    </div>
                  )}
                  
                  <div className="course-icon">
                    <IconComponent size={48} />
                  </div>
                  
                  <h3 className="course-title">{course.title}</h3>
                  <p className="course-description">{course.short_description}</p>
                  
                  <div className="course-meta">
                    {course.duration_minutes && (
                      <span className="course-duration">
                        <Clock size={16} />
                        {course.duration_minutes} min
                      </span>
                    )}
                    <span className="course-price">
                      {formatPrice(course.price_cents, course.currency)}
                    </span>
                  </div>
                  
                  <div className="course-cta">
                    {course.has_access ? 'Continue Learning' : 'View Course'}
                  </div>
                </Link>
              );
            })}
          </div>
          
          {courses.length === 0 && !error && (
            <div className="no-courses">
              <Shield size={64} />
              <h3>No courses available yet</h3>
              <p>Check back soon for new cybersecurity courses</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Courses;
